import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Dialog, InputAdornment, ListItemIcon,
  TextField, Typography, CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const SEARCH_DEBOUNCE_MS = 200;
const RESULT_LIMIT = 8;

// Survey tools all live under /survey/:surveyId/..., so they can't be reached
// without picking a survey first — only the genuinely global routes go here.
const STATIC_ACTIONS = [
  { id: 'nav-home', label: 'Home', group: 'Go to', to: '/', icon: HomeIcon, roles: ['stack'] },
  { id: 'nav-quotas', label: 'Quota monitoring', group: 'Go to', to: '/quota-monitoring', icon: BarChartRoundedIcon },
  { id: 'nav-settings', label: 'Settings', group: 'Go to', to: '/settings', icon: SettingsIcon, roles: ['stack'] },
  { id: 'nav-new-project', label: 'New project', group: 'Actions', to: '/add-project', icon: AddIcon, roles: ['stack'] },
];

const CommandPalette = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStack = user?.role === 'stack';

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQuery(input), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);

  // Projects are a stack-only endpoint; external users get the static list only.
  useEffect(() => {
    if (!open || !isStack) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get('/projects/', { params: { search: query || undefined, page_size: RESULT_LIMIT } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        setProjects(Array.isArray(data) ? data : data.results ?? []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setProjects([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, query, isStack]);

  const options = useMemo(() => {
    const actions = STATIC_ACTIONS
      .filter((a) => !a.roles || a.roles.includes(user?.role))
      .map((a) => ({ ...a, key: a.id }));
    const projectOptions = projects.map((p) => ({
      key: `project-${p.id}`,
      id: `project-${p.id}`,
      label: p.name,
      group: 'Projects',
      to: `/project/${p.id}`,
      icon: FolderIcon,
      meta: `${p.survey_count ?? 0} survey${p.survey_count !== 1 ? 's' : ''}`,
    }));
    return [...projectOptions, ...actions];
  }, [projects, user?.role]);

  const close = useCallback(() => {
    setOpen(false);
    setInput('');
    setQuery('');
  }, []);

  const handleSelect = (_, option) => {
    if (!option) return;
    close();
    navigate(option.to);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { borderRadius: 3, position: 'fixed', top: 80, m: 0 } } }}
    >
      <Autocomplete
        open
        disablePortal
        options={options}
        // The server already filtered; re-filtering here would drop the static
        // actions whenever the query only matched project names server-side.
        filterOptions={(opts, state) => {
          const q = state.inputValue.trim().toLowerCase();
          if (!q) return opts;
          return opts.filter(
            (o) => o.group === 'Projects' || o.label.toLowerCase().includes(q)
          );
        }}
        groupBy={(o) => o.group}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={handleSelect}
        inputValue={input}
        onInputChange={(_, value, reason) => { if (reason === 'input') setInput(value); }}
        loading={loading}
        noOptionsText="No matches"
        slotProps={{
          paper: { elevation: 0, sx: { boxShadow: 'none' } },
          popper: { sx: { position: 'static !important', transform: 'none !important', width: '100% !important' } },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            placeholder="Search projects or jump to…"
            variant="standard"
            slotProps={{
              input: {
                ...params.InputProps,
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: loading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null,
              },
            }}
            sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          const Icon = option.icon;
          return (
            <Box component="li" key={key} {...rest} sx={{ display: 'flex', gap: 1, px: 2, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </ListItemIcon>
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                {option.label}
              </Typography>
              {option.meta && (
                <Typography variant="caption" color="text.secondary">
                  {option.meta}
                </Typography>
              )}
            </Box>
          );
        }}
        sx={{ '& .MuiAutocomplete-listbox': { maxHeight: 380 } }}
      />
    </Dialog>
  );
};

export default CommandPalette;
